import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  App,
  Button,
  Card,
  Col,
  Row,
  Tabs,
  Typography,
  Table,
  Empty,
  Divider,
  Space,
  Tag,
} from 'antd';
import { CheckCircleFilled, CloseCircleFilled, EditOutlined, LeftOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import styled from 'styled-components';

import {
  getClientById,
  getClientFullHistory,
} from '../../services/participant';
import usePermissions from '../../hooks/usePermissions';

const { Title, Text } = Typography;

const PageWrapper = styled.div`
  padding: 24px;
`;

const SectionTitle = styled(Title)`
  margin-bottom: 20px !important;
`;

const InfoGrid = styled(Row)`
  margin-top: 8px;
`;

const InfoItem = styled.div`
  background: #fafafa;
  border-radius: 12px;
  padding: 14px 16px;
  height: 100%;
`;

const Label = styled(Text)`
  display: block;
  font-size: 12px;
  color: #8c8c8c;
`;

const Value = styled(Text)`
  display: block;
  font-size: 15px;
  font-weight: 500;
`;

const InfoBlock = ({ label, value }) => (
  <InfoItem>
    <Label>{label}</Label>
    <Value>{value ?? '-'}</Value>
  </InfoItem>
);

export const PlayerProfileView = ({ data }) => {

  if (!data) return <Empty />;

  return (
    <Card bordered={false}>
      <SectionTitle level={4}>Ficha Deportiva · Jugador</SectionTitle>

      <InfoGrid gutter={[16, 16]}>
        <Col xs={24} md={8}><InfoBlock label="Estado" value={data.state?.description} /></Col>
        <Col xs={24} md={8}><InfoBlock label="Ciudad" value={data.city?.name} /></Col>
        <Col xs={24} md={8}><InfoBlock label="Tipo de cancha" value={data.fieldType?.name} /></Col>

        <Col xs={24} md={8}><InfoBlock label="Posición" value={data.position?.name} /></Col>
        <Col xs={24} md={8}><InfoBlock label="Frecuencia de juego" value={data.playFrequency?.name} /></Col>
        <Col xs={24} md={8}><InfoBlock label="Equipo" value={data.teamName} /></Col>

        <Col xs={24} md={8}><InfoBlock label="Altura" value={data.heightCm ? `${data.heightCm} cm` : '-'} /></Col>
        <Col xs={24} md={8}><InfoBlock label="Pie hábil" value={data.dominantFoot?.name} /></Col>
        <Col xs={24} md={8}><InfoBlock label="Talla de tachones" value={data.cleatsSize} /></Col>
      </InfoGrid>

      <Divider />

      <SectionTitle level={5}>Intereses personales</SectionTitle>
      <Space wrap>
        {data.interests?.length ? (
          data.interests.map(i => (
            <Tag key={i.id} color="#BA955E">{i.name}</Tag>
          ))
        ) : (
          <Text type="secondary">Sin intereses registrados</Text>
        )}
      </Space>

      <Divider />

      <InfoGrid gutter={[16, 16]}>
        <Col xs={24} md={8}><InfoBlock label="Frecuencia de compras online" value={data.purchaseFrequency?.name} /></Col>
        <Col xs={24} md={8}><InfoBlock label="Talla de jersey" value={data.jerseySize?.name} /></Col>
        <Col xs={24} md={8}><InfoBlock label="Talla de calzado" value={data.shoeSize} /></Col>
      </InfoGrid>
    </Card>
  );
};

export const FanProfileView = ({ data }) => {
  if (!data) return <Empty />;

  return (
    <Card bordered={false}>
      <SectionTitle level={4}>Perfil · Aficionado</SectionTitle>

      <InfoGrid gutter={[16, 16]}>
        <Col xs={24} md={6}><InfoBlock label="Estado" value={data.state?.description} /></Col>
        <Col xs={24} md={6}><InfoBlock label="Ciudad" value={data.city?.name} /></Col>
        <Col xs={24} md={6}><InfoBlock label="Equipo" value={data.team?.name} /></Col>
        <Col xs={24} md={6}><InfoBlock label="Motivo de apoyo" value={data.supportReason?.name} /></Col>
      </InfoGrid>

      <Divider />

      <SectionTitle level={5}>Intereses personales</SectionTitle>
      <Space wrap>
        {data.interests?.length ? (
          data.interests.map(i => (
            <Tag key={i.id} color="#BA955E">{i.name}</Tag>
          ))
        ) : (
          <Text type="secondary">Sin intereses registrados</Text>
        )}
      </Space>

      <Divider />

      <InfoGrid gutter={[16, 16]}>
        <Col xs={24} md={8}><InfoBlock label="Frecuencia de compras online" value={data.purchase_frequencies?.name} /></Col>
        <Col xs={24} md={8}><InfoBlock label="Talla de jersey" value={data.jerseySize?.name} /></Col>
        <Col xs={24} md={8}><InfoBlock label="Talla de calzado" value={data.shoeSize} /></Col>
      </InfoGrid>

    </Card>
  );
};

export const OwnerProfileView = ({ data, role }) => {
  if (!data) return <Empty />;

  return (
    <Card bordered={false}>
      <SectionTitle level={4}>Perfil · DT / Organizador</SectionTitle>

      <InfoGrid gutter={[16, 16]}>
        <Col xs={24} md={6}><InfoBlock label="Rol específico" value={role?.name} /></Col>
        {['DIRECTOR_TECNICO', 'DUENO_EQUIPO'].includes(role?.code) && (<>
          <Col xs={24} md={6}><InfoBlock label="Categoría del equipo" value={data.categoryEquipment?.name} /></Col>
          <Col xs={24} md={6}><InfoBlock label="Tamaño de plantilla" value={data.templateSize?.name} /></Col>
          <Col xs={24} md={6}><InfoBlock label="Gestiona uniformes" value={data.manageUniforms ? 'Sí' : 'No'} /></Col>
        </>)}

        {['ORGANIZADOR_LIGA'].includes(role?.code) && (<>
          <Col xs={24} md={6}><InfoBlock label="Nombre de la liga" value={data.leagueName || '-'} /></Col>
          <Col xs={24} md={6}><InfoBlock label="Número aproximado de equipos en tu liga" value={data.teamSize?.name} /></Col>
          <Col xs={24} md={6}><InfoBlock label="Ubicación de las canchas principales" value={data.location?.description} /></Col>
        </>)}
        
      </InfoGrid>

      <Divider />

      <SectionTitle level={5}>Intereses personales</SectionTitle>
      <Space wrap>
        {data.interests?.length ? (
          data.interests.map(i => (
            <Tag key={i.id} color="#BA955E">{i.name}</Tag>
          ))
        ) : (
          <Text type="secondary">Sin intereses registrados</Text>
        )}
      </Space>

      <Divider />

      <InfoGrid gutter={[16, 16]}>
        <Col xs={24} md={8}><InfoBlock label="Frecuencia de compras online" value={data.purchase_frequencies?.name} /></Col>
        <Col xs={24} md={8}><InfoBlock label="Talla de jersey" value={data.jerseySize?.name} /></Col>
        <Col xs={24} md={8}><InfoBlock label="Talla de calzado" value={data.shoeSize} /></Col>
      </InfoGrid>

    </Card>
  );
};

const ParticipantViewPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { message } = App.useApp();

  const { can } = usePermissions();

  const [loading, setLoading] = useState(true);
  const [client, setClient] = useState(null);
  const [history, setHistory] = useState({
    subscriptions: [],
    tokens: [],
    competitions: [],
  });

  useEffect(() => {
    if (!id) return;

    const loadAll = async () => {
      try {
        setLoading(true);
        const [clientData, historyData] = await Promise.all([
          getClientById(id),
          getClientFullHistory(id),
        ]);

        setClient(clientData);
        setHistory({
          subscriptions: historyData.subscriptions || [],
          tokens: historyData.tokens || [],
          competitions: historyData.competitions || [],
        });
      } catch {
        message.error('No se pudo cargar la información del participante');
      } finally {
        setLoading(false);
      }
    };

    loadAll();
  }, [id, message]);

  const subscriptionColumns = [
    { title: 'Membresía', dataIndex: ['plan', 'name'] },
    { title: 'Fecha', dataIndex: 'startDate', render: v => dayjs(v).format('DD/MM/YYYY') },
    { title: 'Pago', dataIndex: 'total', render: v => `$${Number(v).toFixed(2)}` },
    { title: 'Estatus', dataIndex: 'status', render: v => (v === 'ACTIVE' ? 'Activo' : 'Vencido') },
  ];

  const tokenColumns = [
    { title: 'Cantidad', dataIndex: 'quantity' },
    { title: 'Fecha', dataIndex: 'createdAt', render: v => dayjs(v).format('DD/MM/YYYY') },
    { title: 'Pago', dataIndex: 'payment', render: v => (v != null ? `$${Number(v).toFixed(2)}` : '-') },
  ];

  const competitionColumns = [
    { title: 'Competencia', dataIndex: ['competition', 'description'] },
    { title: 'Modalidad', dataIndex: ['competition', 'modality', 'name'] },
    { title: 'Género', dataIndex: ['competition', 'gender', 'name'] },
    { title: 'Fecha', dataIndex: ['competition', 'startDate'], render: v => dayjs(v).format('DD/MM/YYYY') },
    { title: 'Resultado', dataIndex: 'result' },
    { title: 'Puntos', dataIndex: 'totalScore' },
  ];

  const renderProfile = () => {
    if (!client?.role?.code) return <Empty />;

    switch (client.role.code) {
      case 'JUGADOR':
        return <PlayerProfileView data={client.userRolPlayer} />;
      case 'AFICIONADO_PORRA':
        return <FanProfileView data={client.userRolAmateur} />;
      case 'DT_ORGANIZADOR':
        return <OwnerProfileView data={client.userRolOwner} role={client.subRole} />;
      default:
        return <Empty />;
    }
  };

  const tabs = [
    {
      key: 'general',
      label: 'Información general',
      children: (
        <Card bordered={false}>
          <InfoGrid gutter={[16, 16]}>
            <Col xs={24} md={6}><InfoBlock label="Nombre completo" value={`${client?.firstName} ${client?.lastName} ${client?.secondLastName || ''}`} /></Col>
            <Col xs={24} md={6}><InfoBlock label="Correo electrónico" value={client?.email} /></Col>
            <Col xs={24} md={6}><InfoBlock label="Teléfono móvil" value={client?.phone} /></Col>
            <Col xs={24} md={6}><InfoBlock label="Estatus" value={
                    <Tag color={client?.isActive ? 'green' : 'volcano'}>
                        {client?.isActive ? 'Activo' : 'Inactivo'}
                    </Tag>
                }
            />
            </Col>

            <Col xs={24} md={6}><InfoBlock label="Fecha de nacimiento" value={client?.birthDate ? dayjs(client.birthDate).format('DD/MM/YYYY') : '-'} /></Col>
            <Col xs={24} md={6}><InfoBlock label="Género" value={client?.gender?.description} /></Col>
            <Col xs={24} md={6}><InfoBlock label="Rol" value={client?.role?.name} /></Col>
            <Col xs={24} md={6}><InfoBlock label="Estado" value={client?.state?.description} /></Col>
            <Col xs={24} md={6}><InfoBlock label="Código para referir" value={client?.referralCode} /></Col>
            <Col xs={24} md={6}><InfoBlock label="Código de referido" value={client?.referredByCode || 'Sin referido'} /></Col>
            <Col xs={24} md={6}><InfoBlock label="Términos y condiciones" 
                value={
                    client?.acceptTerms ? (
                        <CheckCircleFilled style={{ color: 'green', fontSize: 16 }} />
                    ) : (
                        <CloseCircleFilled style={{ color: 'red', fontSize: 16 }} />
                    )
                }
            />
            </Col>
          </InfoGrid>
        </Card>
      ),
    },
    {
      key: 'profile',
      label: 'Perfil',
      children: renderProfile(),
    },
    {
      key: 'history',
      label: 'Historial',
      children: (
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <Row gutter={[16, 16]}>
            <Col xs={24} md={12}>
              <Title level={5}>Membresías</Title>
              <Table rowKey="id" scroll={{ x: 'max-content' }} columns={subscriptionColumns} dataSource={history.subscriptions} pagination={{ pageSize: 5 }} />
            </Col>
            <Col xs={24} md={12}>
              <Title level={5}>Tokens</Title>
              <Table rowKey="id" scroll={{ x: 'max-content' }} columns={tokenColumns} dataSource={history.tokens} pagination={{ pageSize: 5 }} />
            </Col>
          </Row>

          <Divider />

          <Title level={5}>Competencias</Title>
          <Table rowKey="id" scroll={{ x: 'max-content' }} columns={competitionColumns} dataSource={history.competitions} pagination={{ pageSize: 10 }} />
        </Space>
      ),
    },
  ];

  const handleEdit = () => {
    navigate(`/participants/${id}/edit`);
  };
  const handleBack = () => {
    navigate('/participants/all');
  };

  return (
    <PageWrapper>
      <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Button 
            type="text" 
            icon={<LeftOutlined style={{ fontSize: '24px', strokeWidth: 4 }} />} 
            style={{ 
              padding: 0,
              color: '#BA955E',
              fontSize: '24px',
              fontWeight: '700',
              height: 'auto'
            }}
            onClick={handleBack}
          />
          <Title level={3} style={{ marginBottom: 0 }}>Detalles del participante</Title>
        </div>

        {can('participants.edit') && (<Button 
          type="text" 
          icon={<EditOutlined style={{ fontSize: '24px', strokeWidth: 4 }} />} 
          onClick={handleEdit}
          style={{ 
            padding: 0,
            color: '#262626',
            fontSize: '24px',
            fontWeight: '100',
            height: 'auto'
          }}
        />)}
      </div>

      <Card loading={loading} style={{ marginTop: 16 }}>
        <Tabs items={tabs} />
      </Card>
    </PageWrapper>
  );
};

export default ParticipantViewPage;
